import { useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { ARRAY_ERROR } from 'final-form';
import { FormSpy } from 'react-final-form';
import { v4 as uuidv4 } from 'uuid';
import spacetime from 'spacetime';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';

import { getIsPlanErknmStatusApproved } from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getIsRsnCngPPXXII,
  getKnmFormRef,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsNeedActSaveOrSign,
  getKnmWatchOnlyByTestUser,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { makeRequired } from '../../../../CorePackage/MainModule/validators/isRequired';
import { composeValidators } from '../../../../CorePackage/MainModule/validators/composeValidators';
import { makeNoLaterThenValidator } from '../../../../CorePackage/MainModule/validators/isNoLaterThen';
import { makeMaxNumber } from '../../../../CorePackage/MainModule/validators/maxNumber';
import { makeIsNormalFloat } from '../../../../CorePackage/MainModule/validators/isNormalFloat';
import { formatDate, parseDate } from '../../../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from '../../../../CorePackage/MainModule/constants/dateFormats';
import { Button } from '../../../../UIPackage/MainModule/components/Button';
import styles from './index.module.scss';
import { IKnmErknmFormRescheduling } from '../../../MainModule/interfaces/IKnmErknmFormRescheduling';
import { KnmCollapse } from '../../../../KnmPackage/MainModule/components/KnmCollapse';
import { makeMinNumber } from '../../../../CorePackage/MainModule/validators/minNumber';
import { getUserRoleIsSupervisor } from '../../../../AuthPackage/MainModule/selectors/authSelectors';
import { checkHasErrorInCollapseBlock } from '../../../../functions/checkHasErrorInCollapseBlock';

interface IErknmInfoProps {
  watchOnly: boolean;
}

interface IErknmInfoStoreProps {
  formRef: IKnmFormRef | undefined;
  isSupervisor: boolean;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isWatchOnlyByTestUser: boolean;
  isNeedActSaveOrSign?: boolean;
  isRsnCngPPXXII: boolean;
}

const ErknmReschedulingWithoutStore = ({
  watchOnly,
  isSupervisor,
  formRef,
  isKnmStatusTypeWaitingCarryOut,
  knmPlanStatus,
  isWatchOnlyByTestUser,
  isNeedActSaveOrSign = false,
  isRsnCngPPXXII,
}: IErknmInfoProps & IErknmInfoStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const remove = formRef?.current.mutators.remove;
  const push = formRef?.current.mutators.push;

  const isKnmPlanStatusApproved = getIsPlanErknmStatusApproved(knmPlanStatus);

  const startDate = formRef?.current.getFieldState('startDate')?.value;

  // Ограничения на даты начала и окончания КНМ в зависимости от параметров
  const knmStartMinDate = useRef<Date>();
  const knmStartExcludeDates = useRef<Date[]>();
  const knmEndExcludeDates = useRef<Date[]>();

  /**
   * Функция удаления переноса
   */
  const makeRemoveReschedulingHandler = useCallback(
    (index: number) => () => !watchOnly && remove && remove(`rescheduling`, index),
    [formRef, remove, watchOnly],
  );

  /**
   * Функция добавления переноса
   */
  const addRescheduling = useCallback(() => {
    if (push) {
      const oldStartDate = formRef.current.getFieldState('startDate')?.value;
      const oldEndDate = formRef.current.getFieldState('stopDate')?.value;

      const addingReschedule: IKnmErknmFormRescheduling = {
        listId: uuidv4(),
        isNew: true,
        oldStartDate,
        oldEndDate,
        guid: undefined,
      };

      push('rescheduling', addingReschedule);
    }
  }, [formRef, push, watchOnly]);

  return (
    <FormSpy subscription={{ values: true }}>
      {({ values }) => {
        const rescheduling = values.rescheduling || [];
        const isDisabledButtonAddNewRecheduling =
          !!rescheduling && rescheduling.some((i: IKnmErknmFormRescheduling) => i.isNew);

        return (
          <FieldBlock
            label="Сведения о переносе проведения мероприятия"
            button={
              <Button
                className="shared-btn-add"
                text="Добавить"
                type="button"
                variant="link"
                color="primary"
                onClick={addRescheduling}
                disabled={
                  isWatchOnlyByTestUser ||
                  !isSupervisor ||
                  isDisabledButtonAddNewRecheduling ||
                  !isKnmStatusTypeWaitingCarryOut ||
                  !isKnmPlanStatusApproved ||
                  isNeedActSaveOrSign ||
                  isRsnCngPPXXII
                }
              />
            }
            className={styles.FullWidth}
          >
            <FormSpy subscription={{ errors: true, touched: true }}>
              {props => {
                const fieldError = props.errors?.rescheduling?.[ARRAY_ERROR];
                const isFieldTouched = props.touched?.rescheduling;

                if (isFieldTouched && fieldError) {
                  return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
                }

                return null;
              }}
            </FormSpy>
            <FieldArray name={'rescheduling'}>
              {({ fields }: FieldArrayRenderProps<IKnmErknmFormRescheduling, HTMLElement>) => {
                if (!fields.length) {
                  return null;
                }

                return fields.map((reschedulingFieldName, sIndex) => {
                  const fieldValue: IKnmErknmFormRescheduling = fields.value?.[sIndex];

                  const isDisableByNeedSaveOrSingAct = isNeedActSaveOrSign;
                  const initiationDate = fieldValue?.initiationDate;
                  const isDisabledNewDurationDays = !!fieldValue?.newDurationHours;
                  const isDisabledNewDurationHours = !!fieldValue?.newDirectDurationDays;
                  const minDateStart = fieldValue?.oldStartDate
                    ? parseDate({ date: fieldValue.oldStartDate || '', parseFormat: DATE_FORMAT_LONG_FNS })
                    : knmStartMinDate.current;
                  const maxDateStart = spacetime(
                    formatDate({ date: startDate || '', dateFormat: SYSTEM_DATE_FORMAT_FNS }),
                  )
                    .endOf('year')
                    .toNativeDate();

                  const minDateEnd = fieldValue?.newStartDate
                    ? new Date(formatDate({ date: fieldValue.newStartDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }))
                    : knmStartMinDate.current;

                  const hasError = checkHasErrorInCollapseBlock(formRef, reschedulingFieldName);

                  return (
                    <KnmCollapse
                      id={`rescheduling[${fieldValue.listId}]`}
                      theme="fields"
                      title={`Перенос проведения мероприятия ${initiationDate ? `от ${initiationDate}` : ''} `}
                      isNew={fieldValue.isNew}
                      opened={fieldValue.isNew}
                      onClose={
                        !watchOnly && !isDisableByNeedSaveOrSingAct && fieldValue.isNew
                          ? makeRemoveReschedulingHandler(sIndex)
                          : undefined
                      }
                      key={`rescheduling[${sIndex}]`}
                      changeOpenedCallback={e =>
                        setKnmCollapseOpened(state => ({
                          ...state,
                          [reschedulingFieldName]: e,
                        }))
                      }
                      error={hasError && knmCollapseOpened[reschedulingFieldName]}
                    >
                      <>
                        <Row>
                          <FieldBlock label={'Новая дата начала КНМ'} size="half" placementLabel="left" color="black">
                            <DatePickerField
                              placeholder="Выберите дату"
                              className={styles.RescheduilngNewDates}
                              id={`${reschedulingFieldName}.newStartDate`}
                              name={`${reschedulingFieldName}.newStartDate`}
                              disabled={!fieldValue.isNew || isDisableByNeedSaveOrSingAct}
                              minDate={minDateStart}
                              maxDate={maxDateStart}
                              excludeDates={knmStartExcludeDates.current}
                              validate={composeValidators(
                                makeNoLaterThenValidator(
                                  'Дата начала должна быть меньше или равна дате окончания',
                                  () => fieldValue?.newEndDate,
                                ),
                                makeRequired('Поле не может быть пустым'),
                              )}
                            />
                          </FieldBlock>
                          <FieldBlock
                            label={'Новая дата окончания КНМ'}
                            size="half"
                            placementLabel="left"
                            color="black"
                          >
                            <DatePickerField
                              placeholder="Выберите дату"
                              className={styles.RescheduilngNewDates}
                              id={`${reschedulingFieldName}.newEndDate`}
                              name={`${reschedulingFieldName}.newEndDate`}
                              disabled={!fieldValue.isNew || !fieldValue?.newStartDate || isDisableByNeedSaveOrSingAct}
                              excludeDates={knmEndExcludeDates.current}
                              minDate={minDateEnd}
                            />
                          </FieldBlock>
                        </Row>
                        <Row>
                          <FieldBlock
                            label={'Дата начала до переноса КНМ'}
                            size="half"
                            placementLabel="left"
                            color="black"
                          >
                            <TextInputField
                              id={`${reschedulingFieldName}.oldStartDate`}
                              name={`${reschedulingFieldName}.oldStartDate`}
                              disabled={true}
                            />
                          </FieldBlock>
                          <FieldBlock
                            label={'Дата окончания до переноса КНМ'}
                            size="half"
                            placementLabel="left"
                            color="black"
                          >
                            <TextInputField
                              id={`${reschedulingFieldName}.oldEndDate`}
                              name={`${reschedulingFieldName}.oldEndDate`}
                              disabled={true}
                            />
                          </FieldBlock>
                        </Row>
                        <Row>
                          <FieldBlock
                            label={'Новый срок непосредственного взаимодействия (дней)'}
                            placementLabel="left"
                            color="black"
                            size="half"
                          >
                            <TextInputField
                              id={`${reschedulingFieldName}.newDirectDurationDays`}
                              name={`${reschedulingFieldName}.newDirectDurationDays`}
                              disabled={
                                watchOnly ||
                                isDisabledNewDurationDays ||
                                !fieldValue.isNew ||
                                isDisableByNeedSaveOrSingAct
                              }
                              validate={composeValidators(
                                makeIsNormalFloat('Введены недопустимые символы'),
                                makeMinNumber(1, 'Число не может быть меньше 1'),
                                makeMaxNumber(365, 'Число не может превышать 365'),
                              )}
                            />
                          </FieldBlock>
                          <FieldBlock
                            label={'Новый срок непосредственного взаимодействия (часов)'}
                            placementLabel="left"
                            color="black"
                            size="half"
                          >
                            <TextInputField
                              id={`${reschedulingFieldName}.newDurationHours`}
                              name={`${reschedulingFieldName}.newDurationHours`}
                              disabled={
                                watchOnly ||
                                isDisabledNewDurationHours ||
                                !fieldValue.isNew ||
                                isDisableByNeedSaveOrSingAct
                              }
                              validate={composeValidators(
                                makeIsNormalFloat('Введены недопустимые символы'),
                                makeMinNumber(1, 'Число не может быть меньше 1'),
                                makeMaxNumber(256, 'Число не может превышать 256'),
                              )}
                            />
                          </FieldBlock>
                        </Row>
                        <Row>
                          <FieldBlock label={'Комментарий'} labelSize="col-3" color="black">
                            <TextInputField
                              id={`${reschedulingFieldName}.comment`}
                              name={`${reschedulingFieldName}.comment`}
                              disabled={watchOnly || !fieldValue.isNew || isDisableByNeedSaveOrSingAct}
                            />
                          </FieldBlock>
                        </Row>
                      </>
                    </KnmCollapse>
                  );
                });
              }}
            </FieldArray>
          </FieldBlock>
        );
      }}
    </FormSpy>
  );
};

const mapStateToProps = (state: IRootState): IErknmInfoStoreProps => ({
  formRef: getKnmFormRef(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  isRsnCngPPXXII: getIsRsnCngPPXXII(state),
});

export const ErknmRescheduling = connect(mapStateToProps)(ErknmReschedulingWithoutStore);
